import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Bell from '../../macadamia-core/icons/Bell';

// Utiliser les mêmes interfaces que votre composant qui fonctionne
interface Notification {
    _id: string;
    message: string;
    status: 'unread' | 'read';
    createdAt: string;
    type: string;
    relatedEntityId?: string;
}

// Props similaires à NotificationList mais adaptées pour le dropdown
interface NotificationDropdownProps {
    notifications: Notification[];
    isLoading: boolean;
    hasMore: boolean;
    loadMore: () => void;
    markAsRead: (id: string) => void;
    unreadCount: number;
    onOpen?: () => void; // Callback quand on ouvre le dropdown
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
    notifications,
    isLoading,
    hasMore,
    loadMore,
    markAsRead,
    unreadCount,
    onOpen
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Gestion du clic en dehors
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    // Fonction pour obtenir l'URL de notification (même logique que votre composant)
    const getNotificationUrl = (notification: Notification): string => {
        switch (notification.type) {
            case 'nouvelle_jurisprudence_similaire':
            case 'nouvelle_jurisprudence_similaire_detail':
                return '/jurisprudences/suivi';

            case 'new_follower':
                return notification.relatedEntityId
                    ? `/profil/${notification.relatedEntityId}`
                    : '#';

            default:
                return notification.relatedEntityId
                    ? `/dossiers/${notification.relatedEntityId}`
                    : '#';
        }
    };

    // Fonction pour formater la date (même que votre composant)
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            return 'Date non disponible';
        }
        return date.toLocaleString();
    };

    const handleToggle = () => {
        setIsOpen(!isOpen);
        if (!isOpen && onOpen) {
            // Callback pour charger les notifications
            onOpen();
        }
    };

    const handleNotificationClick = (notification: Notification) => {
        if (notification.status === 'unread') {
            markAsRead(notification._id);
        }
        setIsOpen(false); // Fermer le dropdown après clic
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={handleToggle}
                className="p-2 rounded-full hover:bg-mcdm-primary-200 dark:hover:bg-mcdm-third-300 
                         text-mcdm-third-500 dark:text-mcdm-secondary-400
                         transition-colors duration-200 relative"
                aria-label="Notifications"
            >
                <Bell stroke="currentColor" fill="none" width="20px" height="20px" />
                {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute right-0 mt-2 w-[260px] bg-white dark:bg-mcdm-third-400 shadow-sm dark:shadow-sm shadow-inner dark:shadow-inner
                                 rounded-lg shadow-lg py-2 z-20 max-h-96 overflow-hidden flex flex-col"
                    >
                        {/* Header */}
                        <div className="px-4 py-2">
                            <div className="text-sm font-medium text-mcdm-third-500 dark:text-mcdm-primary-200 flex items-center justify-between">
                                <span>Notifications</span>
                                {unreadCount > 0 && (
                                    <span className="text-xs bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-300 px-2 py-1 rounded-full">
                                        {unreadCount} nouvelle{unreadCount > 1 ? 's' : ''}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto">
                            {/* Loading initial */}
                            {isLoading && notifications.length === 0 && (
                                <div className="p-4">
                                    {Array.from({ length: 3 }).map((_, index) => (
                                        <div key={index} className="animate-pulse flex flex-col space-y-2 mb-4">
                                            <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded"></div>
                                            <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-3/4"></div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Empty state */}
                            {!isLoading && notifications.length === 0 && (
                                <div className="p-8 text-center">
                                    <div className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-4">
                                        <Bell stroke="currentColor" fill="none" width="48px" height="48px" />
                                    </div>
                                    <span className="text-gray-400 text-sm mt-1">...zZz...</span>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                        Aucune notification
                                    </p>
                                </div>
                            )}

                            {/* Notifications list - même logique que votre NotificationList */}
                            {notifications.map((notification, index) => {
                                const notificationUrl = getNotificationUrl(notification);
                                const isUnread = notification.status === 'unread';

                                const NotificationContent = () => (
                                    <div
                                        className={`px-4 py-3 border-b border-gray-100 dark:border-gray-600 last:border-b-0
                                                  ${isUnread
                                                ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-l-blue-500'
                                                : 'hover:bg-gray-50 dark:hover:bg-mcdm-third-300'
                                            } 
                                                  transition-colors duration-150 cursor-pointer`}
                                        onClick={() => handleNotificationClick(notification)}
                                    >
                                        <div className="flex items-start space-x-2">
                                            <div className="flex-1 min-w-0">
                                                <div className={`text-sm mb-2 ${isUnread
                                                    ? 'font-medium text-mcdm-third-600 dark:text-mcdm-primary-100'
                                                    : 'text-mcdm-third-500 dark:text-mcdm-primary-200'
                                                    }`}>
                                                    {notification.message}
                                                </div>
                                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                                    {formatDate(notification.createdAt)}
                                                </div>
                                            </div>
                                            {isUnread && (
                                                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                                            )}
                                        </div>
                                    </div>
                                );

                                return (
                                    <div key={notification._id || index}>
                                        {notificationUrl !== '#' ? (
                                            <Link to={notificationUrl} className="block">
                                                <NotificationContent />
                                            </Link>
                                        ) : (
                                            <NotificationContent />
                                        )}
                                    </div>
                                );
                            })}

                            {/* Load more */}
                            {hasMore && (
                                <div className="p-4 text-center border-t border-gray-200 dark:border-gray-600">
                                    <button
                                        onClick={loadMore}
                                        disabled={isLoading}
                                        className="text-sm text-mcdm-secondary-500 dark:text-mcdm-secondary-400 
                                                 hover:text-mcdm-secondary-600 dark:hover:text-mcdm-secondary-300
                                                 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isLoading ? 'Chargement...' : 'Charger plus'}
                                    </button>
                                </div>
                            )}

                            {/* Loading more */}
                            {isLoading && notifications.length > 0 && (
                                <div className="p-4 text-center text-gray-500">
                                    Chargement...
                                </div>
                            )}
                        </div>

                        {/* Lien vers toutes les notifications */}
                        <Link
                            to="/notifications"
                            onClick={() => setIsOpen(false)}
                            className="px-4 py-3 text-center text-sm font-medium text-mcdm-secondary-500 dark:text-mcdm-secondary-400
                                     hover:bg-gray-50 dark:hover:bg-mcdm-third-300 border-t border-gray-100 dark:border-gray-600"
                        >
                            Toutes les notifications
                        </Link>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default NotificationDropdown;